import axios from 'axios';
import { authService } from './authService';

const api = axios.create({
  baseURL: `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate API instance for anonymous/draft operations
const anonymousApi = axios.create({
  baseURL: `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

anonymousApi.interceptors.request.use(
  (config) => {
    const tokens = authService.getStoredTokens();
    if (tokens?.idToken) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${tokens.idToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor to add the auth token
api.interceptors.request.use(
  (config) => {
    const isDraftOperation = config.headers && config.headers['x-anonymous-draft'];

    if (!isDraftOperation) {
      const tokens = authService.getStoredTokens();
      if (tokens?.idToken) {
        // Use Cognito ID token for API Gateway authorization
        config.headers['Authorization'] = `Bearer ${tokens.idToken}`;
      }
    } else {
      // Ensure no auth header is sent for draft operations
      if (config.headers && config.headers['Authorization']) {
        console.warn('api: Removing Authorization header for draft operation');
        delete config.headers['Authorization'];
      }
    }
    
    // Remove the draft marker header before sending
    if (config.headers && config.headers['x-anonymous-draft']) {
      delete config.headers['x-anonymous-draft'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && isRefreshCall) {
      authService.clearStoredAuth();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const tokens = authService.getStoredTokens();

      if (tokens?.refreshToken) {
        try {
          const response = await authService.refreshToken(tokens.refreshToken);
          if (response.success && response.data) {
            authService.storeTokens(response.data.tokens);
            originalRequest.headers['Authorization'] = `Bearer ${response.data.tokens.idToken}`;
            return api(originalRequest);
          } else {
            throw new Error('Token refresh failed');
          }
        } catch (refreshError) {
          authService.clearStoredAuth();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

export { api, anonymousApi };
